/**
 * ForecastSection — predictive cards for the Home page.
 *
 * Layout: two rows.
 *   Row 1: 3 revenue projections (end-of-today / week / month)
 *   Row 2: 3 top-performer leaderboards (creators / fans / brands)
 *
 * Each card shows a confidence badge (HIGH / MEDIUM / LOW) so the user
 * trusts the right ones — early-month month-end forecasts are honestly
 * MEDIUM, not faked-high.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useForecast } from "./useForecast.v2";
import { SkelSection } from "./PageState.v2";

function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}

const CONF_STYLE = {
  high:   { label: "High confidence",   bg: "rgba(34,197,94,0.12)",  fg: "#0E7B47" },
  medium: { label: "Medium confidence", bg: "rgba(252,128,25,0.14)", fg: "#9C5119" },
  low:    { label: "Low confidence",    bg: "rgba(239,68,68,0.10)",  fg: "#A82A2A" },
};

function ConfidenceBadge({ level }) {
  const s = CONF_STYLE[level] || CONF_STYLE.medium;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 999,
      background: s.bg, color: s.fg,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

function ProjectionCard({ title, currentSoFar, projected, low, high, confidence, basis, accent = "#FC8019" }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: 14, padding: 16,
      display: "flex", flexDirection: "column", gap: 8,
      boxShadow: "0 1px 2px rgba(31,42,63,0.04)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted }}>{title}</span>
        <ConfidenceBadge level={confidence} />
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: T.text.primary, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
        {formatINR(projected)}
      </div>
      <div style={{ fontSize: 11, color: T.text.muted, fontWeight: 600 }}>
        Range: <span style={{ color: T.text.secondary, fontWeight: 700 }}>{formatINR(low)} – {formatINR(high)}</span>
      </div>
      <div style={{ fontSize: 11, color: T.text.muted }}>
        So far: <span style={{ color: T.text.primary, fontWeight: 700 }}>{formatINR(currentSoFar)}</span>
      </div>
      <div style={{ fontSize: 10, color: T.text.muted, marginTop: 4, fontStyle: "italic", lineHeight: 1.35 }}>
        {basis}
      </div>
    </div>
  );
}

function MomentumChip({ momentum }) {
  if (momentum == null || !Number.isFinite(momentum)) return null;
  const pct = momentum * 100;
  const up = pct > 0.5, down = pct < -0.5;
  const fg = up ? "#0E7B47" : down ? "#A82A2A" : T.text.muted;
  const sign = up ? "▲" : down ? "▼" : "•";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: fg, fontVariantNumeric: "tabular-nums" }}>
      {sign} {pct >= 0 ? "+" : ""}{pct.toFixed(0)}%
    </span>
  );
}

function LeaderboardCard({ title, rows, confidence, basis, accent = "#FC8019" }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: 14, padding: 16,
      display: "flex", flexDirection: "column", gap: 8,
      boxShadow: "0 1px 2px rgba(31,42,63,0.04)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted }}>{title}</span>
        <ConfidenceBadge level={confidence} />
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: T.text.muted, padding: "10px 0" }}>No data yet for this month.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((r, i) => (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "auto 1fr auto auto",
              alignItems: "center", gap: 10,
              padding: "8px 0",
              borderBottom: i < rows.length - 1 ? `1px solid ${T.border.subtle}` : "none",
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: "50%",
                background: i === 0 ? accent : T.surface.canvas,
                color: i === 0 ? "#FFFFFF" : T.text.primary,
                fontSize: 11, fontWeight: 800,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>{i + 1}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
              <MomentumChip momentum={r.momentum} />
              <span style={{ fontSize: 12, fontWeight: 700, color: T.text.primary, fontVariantNumeric: "tabular-nums" }}>{formatINR(r.projected)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 10, color: T.text.muted, marginTop: 4, fontStyle: "italic", lineHeight: 1.35 }}>
        {basis}
      </div>
    </div>
  );
}

export function ForecastSection() {
  const { data, loading, error } = useForecast();

  if (loading) {
    // Skeleton matching the section's normal height — no text-only "Calculating…"
    return <div style={{ marginBottom: 14 }}><SkelSection height={220} /></div>;
  }
  if (error || !data) return null; // silent — section drops if backend fails

  const r = data.revenue || {};
  const tc = data.topCreators || { rows: [], confidence: "low", basis: "" };
  const tf = data.topFans     || { rows: [], confidence: "low", basis: "" };
  const tb = data.topBrands   || { rows: [], confidence: "low", basis: "" };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, padding: "0 4px" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Forecast</div>
          <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>
            Projections from live data — confidence badge tells you how much to trust each one
          </div>
        </div>
      </div>

      {/* Revenue projections row */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
        gap: 12, marginBottom: 12,
      }}>
        {r.endOfToday && (
          <ProjectionCard title="End of Today — Inflow"
            currentSoFar={r.endOfToday.currentSoFar}
            projected={r.endOfToday.projected}
            low={r.endOfToday.low} high={r.endOfToday.high}
            confidence={r.endOfToday.confidence}
            basis={r.endOfToday.basis}
            accent="#FC8019" />
        )}
        {r.endOfWeek && (
          <ProjectionCard title="End of This Week — Inflow"
            currentSoFar={r.endOfWeek.currentSoFar}
            projected={r.endOfWeek.projected}
            low={r.endOfWeek.low} high={r.endOfWeek.high}
            confidence={r.endOfWeek.confidence}
            basis={r.endOfWeek.basis}
            accent="#7C4DFF" />
        )}
        {r.endOfMonth && (
          <ProjectionCard title="End of This Month — Inflow"
            currentSoFar={r.endOfMonth.currentSoFar}
            projected={r.endOfMonth.projected}
            low={r.endOfMonth.low} high={r.endOfMonth.high}
            confidence={r.endOfMonth.confidence}
            basis={r.endOfMonth.basis}
            accent="#0E7B47" />
        )}
      </div>

      {/* Top performer projections row */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
        gap: 12,
      }}>
        <LeaderboardCard title="Top creators projected — this month"
          rows={tc.rows} confidence={tc.confidence} basis={tc.basis} accent="#4CAF50" />
        <LeaderboardCard title="Top fans projected — this month"
          rows={tf.rows} confidence={tf.confidence} basis={tf.basis} accent="#7C4DFF" />
        <LeaderboardCard title="Top brands projected — this month"
          rows={tb.rows} confidence={tb.confidence} basis={tb.basis} accent="#FF6B9D" />
      </div>
    </div>
  );
}
